import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import api from '@/lib/api';

interface DnsRecord {
  type: string;
  name: string;
  value: string;
  priority?: number;
  status?: string;
}

interface DomainStatus {
  configured: boolean;
  domain: string;
  status: string;
  verified: boolean;
  records: DnsRecord[];
  /** true = we manage the DNS (records auto-added, propagating); false = tenant adds them; null = unknown. */
  dnsManaged: boolean | null;
  /** Apex `v=spf1` record present? null = DNS unreachable, so we say nothing. */
  apexSpf: boolean | null;
  /** include/redirect targets with no SPF record; any one makes the whole record fail. */
  spfBroken?: string[];
  /** The apex record to publish, built by the server for this zone. */
  suggestedSpf?: string;
}

/** Only for an older API that doesn't send its own suggestion. */
const FALLBACK_SPF = 'v=spf1 include:amazonses.com ~all';

/**
 * Notification shown in the tenant admin when their Resend email domain isn't
 * fully verified. Renders NOTHING when the domain is verified, unconfigured, or
 * still loading — it's a call to action, not a permanent status card.
 *
 * When we manage the domain's DNS (rumio.world subdomain or a zone in our
 * Cloudflare account) the records were added automatically and just need to
 * propagate, so we reassure rather than instruct. When we DON'T (a custom domain
 * whose DNS lives elsewhere, e.g. a Cloudflare account we can't reach), we show
 * the exact records for the owner to add at their DNS provider.
 *
 * A MISSING APEX SPF RECORD IS REPORTED EVEN WHEN RESEND SAYS VERIFIED. Resend
 * authenticates with DKIM, so a domain passes there with no SPF record at all —
 * but Sign in with Apple then drops every message to a `@privaterelay.appleid.com`
 * alias, silently, with no bounce. Before this, that tenant saw nothing: Resend
 * green, no failed sends, and customers who chose "Hide My Email" simply never
 * received their order confirmations.
 */
export default function EmailDomainAlert() {
  const { data } = useQuery({
    queryKey: ['admin', 'email-domain-status'],
    queryFn: () => api.get<DomainStatus>('/admin/email-setup/domain-status'),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Two independent problems, either of which is worth interrupting for.
  const resendUnverified =
    !!data && data.configured && !data.verified &&
    Array.isArray(data.records) && data.records.length > 0;
  // Strictly false, never null: DNS was reachable and there is genuinely no SPF
  // record. An unreachable lookup must not accuse a tenant who is fine.
  const spfMissing = !!data && data.configured && data.apexSpf === false;
  // A record that points at a name with no SPF fails every check, same as no record.
  const spfBroken = !!data && data.configured && data.apexSpf === true ? (data.spfBroken ?? []) : [];
  const spfProblem = spfMissing || spfBroken.length > 0;

  // Only a call to action when there's genuinely an action to take.
  if (!data || (!resendUnverified && !spfProblem)) {
    return null;
  }

  const managed = data.dnsManaged === true;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" role="alert">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            {resendUnverified
              ? `Your email domain ${data.domain} isn’t fully verified yet`
              : spfBroken.length > 0
                ? `${data.domain} has a broken SPF record`
                : `${data.domain} is missing an SPF record`}
          </p>
          {resendUnverified && (
            <p className="mt-1 text-sm text-amber-800">
              {managed
                ? "We've added the required DNS records for you — they can take a few minutes to propagate. If it doesn't clear on its own shortly, re-check from Email Setup."
                : 'Add these DNS records at your DNS provider so your store can send and receive email reliably:'}
            </p>
          )}

          {spfProblem && (
            <div className={resendUnverified ? 'mt-3' : 'mt-1'}>
              <p className="text-sm text-amber-800">
                Customers who sign in with Apple and choose{' '}
                <strong>Hide My Email</strong> are not receiving your emails.
                Apple drops messages from a domain without a working SPF record
                without sending a bounce, so these failures are invisible — nothing
                appears in your order or email logs.
              </p>
              {spfBroken.length > 0 && (
                <p className="mt-2 text-sm text-amber-800">
                  Your record includes{' '}
                  <span className="font-mono">{spfBroken.join(', ')}</span>, which
                  has no SPF record of its own, so every check of your record fails.
                </p>
              )}
              <p className="mt-2 text-sm text-amber-800">
                {spfBroken.length > 0 ? 'Replace the TXT record' : 'Add this TXT record'} at your DNS provider on{' '}
                <span className="font-mono">{data.domain}</span>:
              </p>
              <div className="mt-2 overflow-x-auto rounded-md border border-amber-200 bg-white/60">
                <table className="min-w-full text-left text-xs">
                  <thead className="text-amber-700">
                    <tr className="border-b border-amber-200">
                      <th className="px-3 py-1.5 font-medium">Type</th>
                      <th className="px-3 py-1.5 font-medium">Name / Host</th>
                      <th className="px-3 py-1.5 font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-amber-900">
                    <tr className="align-top">
                      <td className="px-3 py-1">TXT</td>
                      <td className="px-3 py-1">@</td>
                      <td className="px-3 py-1 break-all">
                        {data.suggestedSpf || FALLBACK_SPF}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {resendUnverified && !managed && (
            <div className="mt-3 overflow-x-auto rounded-md border border-amber-200 bg-white/60">
              <table className="min-w-full text-left text-xs">
                <thead className="text-amber-700">
                  <tr className="border-b border-amber-200">
                    <th className="px-3 py-1.5 font-medium">Type</th>
                    <th className="px-3 py-1.5 font-medium">Name / Host</th>
                    <th className="px-3 py-1.5 font-medium">Value</th>
                    <th className="px-3 py-1.5 font-medium">Priority</th>
                  </tr>
                </thead>
                <tbody className="font-mono text-amber-900">
                  {data.records.map((r, i) => (
                    <tr key={i} className="border-b border-amber-100 last:border-0 align-top">
                      <td className="px-3 py-1">{r.type}</td>
                      <td className="px-3 py-1 break-all">{r.name}</td>
                      <td className="px-3 py-1 break-all">{r.value}</td>
                      <td className="px-3 py-1">{r.type === 'MX' && r.priority != null ? r.priority : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Link
            to="/admin/email-setup"
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-amber-900 hover:underline"
          >
            Go to Email Setup
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}
